import { Card, CardColor, GamePhase, GameState, Player, Tower, CardType } from '../types'
import { shuffle, ensureDealingDeck, drawFromDealingDeck } from './decks'
import { getEffectiveHero } from './heroes'

// Logging disabled to improve FPS
let detailedLogging = false

const log = (tag: string, message: string, data?: any) => {
  if (detailedLogging) {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, 12)
    if (data !== undefined) {
      console.groupCollapsed(`[${timestamp}] [${tag}] ${message}`)
      console.log(JSON.parse(JSON.stringify(data)))
      console.groupEnd()
    } else {
      console.log(`[${timestamp}] [${tag}] ${message}`)
    }
  }
}

export const toggleDiscardMode = (state: GameState, playerId: number): GameState => {
  const currentMode = state.discardMode?.[playerId] || null

  if (state.phase === GamePhase.Preparation) {
    const newMode = currentMode === 'day' ? null : 'day'
    log('toggleDiscardMode', `Player ${playerId} toggled day discard mode to: ${newMode}`)
    return { ...state, discardMode: { ...state.discardMode, [playerId]: newMode } }
  }
  if (state.phase === GamePhase.Combat || state.phase === GamePhase.TowerPurchase) {
    const newMode = currentMode === 'night' ? null : 'night'
    log('toggleDiscardMode', `Player ${playerId} toggled night discard mode to: ${newMode}`)
    return { ...state, discardMode: { ...state.discardMode, [playerId]: newMode } }
  }
  return state
}

export const discardCardForEssence = (state: GameState, playerId: number, cardId: string): GameState => {
  // Note: discardMode check removed - action validity is confirmed by sender before sending
  const playerIndex = state.players.findIndex(p => p.id === playerId)
  if (playerIndex === -1) return state
  const player = state.players[playerIndex]
  const cardIndex = player.hand.findIndex(c => c.id === cardId)
  if (cardIndex === -1 || player.hand[cardIndex].essenceCost) return state
  const card = player.hand[cardIndex]
  log('discardCard', `Player ${playerId} discarded ${card.type} for 1 essence.`)

  // Create new hand without the discarded card (avoid mutation)
  const newHand = player.hand.filter(c => c.id !== cardId)
  const newPlayer = { ...player, hand: newHand, essence: player.essence + 1 }

  const newPlayers = [...state.players] as [Player, Player]
  newPlayers[playerIndex] = newPlayer

  // Deep copy colorDecks to avoid mutation
  const newColorDecks = { ...state.colorDecks }
  newColorDecks[card.color] = [...newColorDecks[card.color], card]
  newColorDecks[card.color] = shuffle(newColorDecks[card.color], state.seed + '_discard_day_' + card.id)

  if ((state.phase === GamePhase.Combat || state.phase === GamePhase.TowerPurchase) && newPlayer.hero === 'ASTRID') {
    const current = newPlayer.astridCharges?.[card.color] || 0
    newPlayer.astridCharges = { ...(newPlayer.astridCharges || {}), [card.color]: current + 1 }
  }

  // [REWARD] Log discard for essence
  const newLog = [...state.log, `[REWARD] DISCARD_ESSENCE:PLAYER=${playerId}`]

  return { ...state, players: newPlayers, colorDecks: newColorDecks, log: newLog }
}

export const discardStashCardForEssence = (state: GameState, playerId: number, cardId: string): GameState => {
  const playerIndex = state.players.findIndex(p => p.id === playerId)
  if (playerIndex === -1) return state
  const player = state.players[playerIndex]
  const handCardIndex = player.hand.findIndex(c => c.id === cardId && c.essenceCost)
  const preparedCardIndex = player.prepared.findIndex(pc => pc.card.id === cardId && pc.card.essenceCost)
  if (handCardIndex === -1 && preparedCardIndex === -1) {
    log('discardStashCard', `Card ${cardId} not found or not a discardable stash card.`)
    return state
  }
  if (handCardIndex !== -1 && preparedCardIndex === -1) {
    log('discardStashCard', `Card ${cardId} is in hand - stash cards can only be burned from prepared slots.`)
    return state
  }
  const newState = JSON.parse(JSON.stringify(state))
  const newPlayer = newState.players[playerIndex]
  let card: Card
  let essenceGained: number
  let source: string
  card = newPlayer.prepared.splice(preparedCardIndex, 1)[0].card
  essenceGained = 4
  source = 'prepared'
  const dupInHand = newPlayer.hand.findIndex(c => c.id === cardId && c.essenceCost)
  if (dupInHand !== -1) newPlayer.hand.splice(dupInHand, 1)

  const opponent = newState.players.find((p: Player) => p.id !== playerId)!;
  const effectiveHero = getEffectiveHero(newPlayer as Player, opponent);
  const isHibernating = newPlayer.effectCards?.some((ec: { type: string }) => ec.type === CardType.Hibernation);

  // Norim Ability: Every 3rd stash card burned gives double essence (+4 more)
  if (effectiveHero === 'NORIM') {
    const currentCharges = newPlayer.norimCharges || 0;
    const newCharges = currentCharges + 1;

    if (newCharges >= 3) {
      essenceGained += 4; // Double essence (4 base + 4 bonus)
      newPlayer.norimCharges = 0;
      log('norimAbility', `Norim's ability triggered! ${newPlayer.name} gains double essence.`);
      newState.log.push(`[REWARD] HERO_POWER_USED:PLAYER=${playerId}:TYPE=NORIM_DOUBLE_ESSENCE`);
    } else {
      newPlayer.norimCharges = newCharges;
    }
  }

  // Gemata Ability: No essence, draw 3 cards instead
  if (effectiveHero === 'GEMATA') {
    essenceGained = 0
    log('gemataAbility', `Gemata burns stash card but gains NO essence. Drawing 3 cards instead.`)

    // 1. Ensure deck has enough cards using the helper
    // We pass newState which has the current deck state
    const stateWithDeck = ensureDealingDeck(newState, 3)

    // 2. Update our local state with the deck changes
    newState.dealingDeck = stateWithDeck.dealingDeck
    newState.colorDecks = stateWithDeck.colorDecks

    // 3. Manually draw 3 cards from the now-ensured deck
    // We do this manually because drawFromDealingDeck helper would modify a different player object reference
    // and we want to modify 'newPlayer' which already has the stash card removed.
    const cardsToDraw = 3
    for (let i = 0; i < cardsToDraw; i++) {
      const c = newState.dealingDeck.pop()
      if (c) {
        c.ownerId = playerId
        newPlayer.hand.push(c)
      }
    }

    log('gemataAbility', `Gemata drew 3 cards. Hand size: ${newPlayer.hand.length}`)
    newState.log.push(`[REWARD] HERO_POWER_USED:PLAYER=${playerId}:TYPE=GEMATA_DRAW_3`);
  }

  // Trifle Ability: Draw one card from color decks for each tower owned
  if (effectiveHero === 'TRIFLE' && !isHibernating) {
    newPlayer.towers.forEach((tower: Tower) => {
      if (newState.colorDecks[tower.color]?.length > 0) {
        const drawnCard = newState.colorDecks[tower.color].pop()!;
        drawnCard.ownerId = playerId;
        newPlayer.hand.push(drawnCard);
        newState.log.push(`t:log.drawFromTower|playerName=${newPlayer.name}|towerColor=${tower.color}`);
      }
    });
    if (newPlayer.towers.length > 0) {
      log('trifleAbility', `Trifle drew ${newPlayer.towers.length} cards from towers.`);
      newState.log.push(`[REWARD] HERO_POWER_USED:PLAYER=${playerId}:TYPE=TRIFLE_TOWER_DRAW`);
    }
  }

  newPlayer.essence += essenceGained
  newState.burnedStashCards.push(card)
  log('discardStashCard', `Player ${playerId} discarded stash card ${card.type} from ${source} for ${essenceGained} essence.`)

  // [REWARD] Log burning stash card for essence (only at night)
  if (state.phase === GamePhase.Combat || state.phase === GamePhase.TowerPurchase) {
    newState.log.push(`[REWARD] BURN_STASH_ESSENCE:PLAYER=${playerId}`)
  }

  return newState
}

export const discardPreparedColorCardForEssence = (state: GameState, playerId: number, cardId: string): GameState => {
  // Note: discardMode check removed - action validity is confirmed by sender before sending
  const playerIndex = state.players.findIndex(p => p.id === playerId)
  if (playerIndex === -1) return state
  const player = state.players[playerIndex]
  const preparedIndex = player.prepared.findIndex(pc => pc.card.id === cardId && !pc.card.essenceCost)
  if (preparedIndex === -1) return state
  const prepared = player.prepared[preparedIndex]
  if (prepared.lockedState) {
    const roundsSinceLocked = state.round - prepared.lockedState.appliedRound
    if (roundsSinceLocked === 0) {
      log('discardPreparedColor', `Blocked: card ${cardId} is locked (Night 1).`)
      return { ...state, log: [...state.log, `${player.name} cannot discard this locked card yet!`] }
    }
  }
  const card = prepared.card
  const newState = JSON.parse(JSON.stringify(state)) as GameState
  const newPlayer = newState.players[playerIndex]
  newPlayer.prepared.splice(preparedIndex, 1)
  const essenceGain = newPlayer.hero === 'TERRIK' ? 3 : 1
  if (newPlayer.hero === 'TERRIK') {
    newState.log.push(`[REWARD] HERO_POWER_USED:PLAYER=${playerId}:TYPE=TERRIK_TRIPLE_ESSENCE`);
  }
  newPlayer.essence += essenceGain
  newState.colorDecks[card.color].push(card)
  newState.colorDecks[card.color] = shuffle(newState.colorDecks[card.color], newState.seed + '_discard_night_' + card.id)
  if (newPlayer.hero === 'ASTRID') {
    const current = newPlayer.astridCharges?.[card.color] || 0
    newPlayer.astridCharges = { ...(newPlayer.astridCharges || {}), [card.color]: current + 1 }
  }
  log('discardPreparedColor', `Player ${playerId} discarded prepared ${card.type} for ${essenceGain} essence at night.`)

  return newState
}

